import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PorterAvatar } from './porterUi';

const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : d?.message || "Couldn't save."; };

const CrewSize = ({ job, authFetch, onChanged }) => {
  const m = /(\d)\s*man/i.exec(job.van_recommendation || '');
  const men = m ? Math.min(2, parseInt(m[1], 10)) : 2;
  const pick = async (n) => {
    if (n === men) return;
    try { await authFetch('patch', `/admin/estimate/${job.id}/crew-size`, { men: n }); toast.success(`Changed to ${n} man & van.`); onChanged(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="cal-crew-size">
      {[1, 2].map(n => (
        <button key={n} type="button" onClick={() => pick(n)} className={`h-9 px-3 text-xs font-bold ${n === men ? 'bg-[color:var(--mv-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`cal-crew-size-${n}`}>{n} man</button>
      ))}
    </div>
  );
};

const PorterPicker = ({ job, authFetch, onChanged }) => {
  const [list, setList] = useState(null);
  const open = () => authFetch('get', `/admin/crew/replacements?quote_id=${job.id}`).then(({ data }) => setList(data.porters)).catch(() => setList([]));
  const assign = async (pid) => {
    if (!pid) return;
    try { await authFetch('post', '/admin/crew/assign', { porter_id: pid, quote_id: job.id }); toast.success('Porter added for the whole day.'); setList(null); onChanged(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!list) return <button type="button" onClick={open} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-xs font-bold text-[color:var(--mv-navy)]" data-testid="cal-crew-add">+ Add porter</button>;
  return (
    <select autoFocus defaultValue="" onChange={(e) => assign(e.target.value)} onBlur={() => setList(null)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="cal-crew-select">
      <option value="">{list.length ? 'Choose a free porter…' : 'No porters free'}</option>
      {list.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
};

export const JobCrewSummary = ({ job, authFetch, onChanged }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => authFetch('get', `/jobs/${job.id}/crew`).then(({ data }) => setD(data)).catch(() => {}), [authFetch, job.id]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };
  if (!d) return null;
  return (
    <div className="pt-3 border-t border-slate-100" data-testid="cal-modal-crew">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Crew · {d.porters_required ? `${d.crew.length}/${d.porters_required} porters` : `${d.crew.length} porter${d.crew.length === 1 ? '' : 's'} (1 man job)`}{d.van ? ` · ${d.van.name || d.van.reg}` : ''}</div>
        <Link to="/admin/porters" className="text-[11px] font-bold text-[color:var(--mv-orange)]" data-testid="cal-modal-crew-manage">Manage crew →</Link>
      </div>
      {d.porters_short > 0 && <div className="text-[11px] font-bold text-amber-700 mt-1" data-testid="cal-modal-crew-short">Needs {d.porters_short} more porter{d.porters_short === 1 ? '' : 's'}</div>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <CrewSize job={job} authFetch={authFetch} onChanged={changed} />
        {d.crew.map(c => (
          <span key={c.assignment.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 rounded-full pl-1 pr-2 py-0.5" data-testid={`cal-crew-porter-${c.porter.id}`}><PorterAvatar p={c.porter} size={20} />{c.porter.name}{c.assignment.timesheet.hours != null ? ` · ${c.assignment.timesheet.hours}h` : ''}</span>
        ))}
        <PorterPicker job={job} authFetch={authFetch} onChanged={changed} />
      </div>
    </div>
  );
};
